import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { slidesApi } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { ArrowLeft, Presentation, Plus, Trash2, Share2, Clock, Search } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';

export default function SlidesHub() {
  const navigate = useNavigate();
  const [presentations, setPresentations] = useState({ owned: [], shared: [] });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showShare, setShowShare] = useState(null);
  const [shareEmail, setShareEmail] = useState('');

  useEffect(() => {
    slidesApi.list().then(d => { setPresentations(d); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const handleCreate = async () => {
    try {
      const p = await slidesApi.create({ title: 'Untitled Presentation' });
      navigate(`/slides/${p.presentation_id}`);
    } catch { toast.error('Failed to create'); }
  };

  const handleDelete = async (id, e) => {
    e.preventDefault(); e.stopPropagation();
    if (!window.confirm('Delete this presentation?')) return;
    try {
      await slidesApi.delete(id);
      setPresentations(p => ({ owned: p.owned.filter(s => s.presentation_id !== id), shared: p.shared }));
      toast.success('Deleted');
    } catch (er) { toast.error(er.message); }
  };

  const handleShare = async () => {
    if (!shareEmail.trim()) return;
    try {
      await slidesApi.share(showShare, shareEmail);
      toast.success('Shared!');
      setShowShare(null); setShareEmail('');
    } catch (e) { toast.error(e.message); }
  };

  const all = [...presentations.owned, ...presentations.shared];
  const filtered = search ? all.filter(p => p.title?.toLowerCase().includes(search.toLowerCase())) : all;

  return (
    <div className="min-h-screen bg-background" data-testid="slides-hub">
      <header className="sticky top-0 z-30 border-b border-border bg-background/95 backdrop-blur">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/suite')}><ArrowLeft className="w-5 h-5" /></Button>
            <Presentation className="w-6 h-6 text-orange-500" />
            <h1 className="text-xl font-bold text-foreground">Slides</h1>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..." className="pl-9 w-48" />
            </div>
            <Button onClick={handleCreate} data-testid="create-slides-btn"><Plus className="w-4 h-4 mr-1" /> New Presentation</Button>
          </div>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-4 py-8">
        {loading ? (
          <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-4">{[1,2,3].map(i => <div key={i} className="h-44 bg-muted animate-pulse rounded-xl" />)}</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20">
            <Presentation className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
            <h2 className="text-xl font-semibold text-foreground mb-2">No presentations yet</h2>
            <p className="text-muted-foreground mb-6">Create your first slide deck.</p>
            <Button onClick={handleCreate}>Create Presentation</Button>
          </div>
        ) : (
          <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-3">
            {filtered.map(p => (
              <div key={p.presentation_id} onClick={() => navigate(`/slides/${p.presentation_id}`)} className="group cursor-pointer" data-testid={`pres-${p.presentation_id}`}>
                <div className="rounded-xl border border-border bg-card hover:shadow-md transition-shadow overflow-hidden">
                  <div className="h-28 bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-500/5 dark:to-amber-500/5 p-3 flex items-center justify-center">
                    <Presentation className="w-10 h-10 text-orange-500" />
                  </div>
                  <div className="p-3">
                    <h3 className="text-sm font-semibold text-foreground truncate group-hover:text-primary transition-colors">{p.title}</h3>
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-[10px] text-muted-foreground flex items-center gap-1"><Clock className="w-3 h-3" />{new Date(p.updated_at).toLocaleDateString()}</span>
                      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={e => { e.stopPropagation(); setShowShare(p.presentation_id); }} className="p-1 hover:bg-muted rounded"><Share2 className="w-3.5 h-3.5 text-muted-foreground" /></button>
                        <button onClick={e => handleDelete(p.presentation_id, e)} className="p-1 hover:bg-muted rounded"><Trash2 className="w-3.5 h-3.5 text-red-400" /></button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
      <Dialog open={!!showShare} onOpenChange={() => setShowShare(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Share Presentation</DialogTitle></DialogHeader>
          <Input value={shareEmail} onChange={e => setShareEmail(e.target.value)} placeholder="Enter user email..." />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowShare(null)}>Cancel</Button>
            <Button onClick={handleShare}>Share</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
